import React, { useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { Button, ProgressBar } from "react-bootstrap";
import {
  IconPlayerPlay,
  IconSearch,
  IconClock,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconCheck,
  IconStar,
  IconBookmark,
  IconLayoutGrid,
} from "@tabler/icons-react";

const VideoCard = ({ video, getImageSrc, onPlay, onImageError }) => (
  <div
    className="crs-card"
    onClick={() => onPlay(video)}
    title={video.title}
  >
    <div className="crs-card__thumb">
      <img
        src={getImageSrc(video)}
        alt={video.title}
        loading="lazy"
        onError={() => onImageError(video.id)}
      />
      <div className="crs-card__overlay">
        <div className="crs-card__play">
          <IconPlayerPlay size={20} />
        </div>
      </div>
      <div className="crs-card__actions">
        <button className="crs-card__action-btn">
          <IconBookmark size={14} />
        </button>
        <button className="crs-card__action-btn">
          <IconStar size={14} />
        </button>
      </div>
      {video.progress_percent > 0 && (
        <div className="crs-card__progress">
          <div
            className="crs-card__progress-fill"
            style={{ width: `${video.progress_percent}%` }}
          />
        </div>
      )}
    </div>
    <div className="crs-card__info">
      <h4 className="crs-card__title">{video.title}</h4>
      <div className="crs-card__meta">
        <span className="crs-card__duration">
          <IconClock size={10} />
          {video.duration_display || "--"}
        </span>
        {video.is_completed && (
          <IconCheck size={14} className="crs-card__check" />
        )}
      </div>
    </div>
  </div>
);

const CategorySection = ({ category, onVideoPlay, isMobile }) => {
  const [imageErrors, setImageErrors] = useState(new Set());
  const [expanded, setExpanded] = useState(false);
  const swiperRef = useRef(null);

  const categoryVideos = category.videos || [];

  const handleImageError = (videoId) => {
    setImageErrors((prev) => new Set(prev).add(videoId));
  };

  const getImageSrc = (video) => {
    if (imageErrors.has(video.id)) {
      return "/placeholder-video.jpg";
    }
    return (
      video.thumbnail_final_url ||
      video.thumbnail_url ||
      "/placeholder-video.jpg"
    );
  };

  if (categoryVideos.length === 0) {
    return (
      <section className="crs-section">
        <div className="crs-section__header">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h2 className="crs-section__title">
              {category.name}
              <span className="crs-section__badge crs-section__badge--empty">
                0/{category.videos_count || 0}
              </span>
            </h2>
          </div>
        </div>
        <div className="crs-empty">
          <div className="crs-empty__icon">
            <IconSearch size={40} />
          </div>
          <h4>Nenhum conteudo disponivel</h4>
          <p>Esta categoria ainda nao possui videos disponiveis.</p>
        </div>
      </section>
    );
  }

  const badgeClass = category.is_completed
    ? "crs-section__badge--completed"
    : category.completion_percentage > 0
    ? "crs-section__badge--progress"
    : "crs-section__badge--empty";

  const swiperConfig = {
    modules: [Navigation, Pagination],
    spaceBetween: 16,
    slidesPerView: 1.2,
    navigation: !isMobile
      ? {
          prevEl: `.crs-arrow-prev-${category.id || "section"}`,
          nextEl: `.crs-arrow-next-${category.id || "section"}`,
        }
      : false,
    breakpoints: {
      480: { slidesPerView: 1.5, spaceBetween: 16 },
      768: { slidesPerView: 2.5, spaceBetween: 20 },
      1024: { slidesPerView: 3, spaceBetween: 20 },
      1200: { slidesPerView: 3, spaceBetween: 20 },
      1400: { slidesPerView: 4, spaceBetween: 20 },
    },
    onSwiper: (swiper) => {
      swiperRef.current = swiper;
    },
  };

  return (
    <section className="crs-section">
      <div className="crs-section__header">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="crs-section__title">
            {category.name}
            <span className={`crs-section__badge ${badgeClass}`}>
              {category.completed_count}/{category.videos_count}
            </span>
          </h2>
          <div className="crs-section__controls">
            <Button
              variant="link"
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <>
                  Ver menos <IconChevronDown size={14} style={{ transform: "rotate(180deg)" }} />
                </>
              ) : (
                <>
                  Ver tudo <IconLayoutGrid size={14} />
                </>
              )}
            </Button>
          </div>
        </div>

        <ProgressBar
          now={category.completion_percentage}
          className={`crs-section__progress ${
            category.is_completed ? "completed" : ""
          }`}
        />
      </div>

      {expanded ? (
        <div className="crs-section__grid">
          {categoryVideos.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              getImageSrc={getImageSrc}
              onPlay={onVideoPlay}
              onImageError={handleImageError}
            />
          ))}
        </div>
      ) : (
        <div className="crs-carousel">
          {!isMobile && (
            <>
              <button
                className={`crs-arrow-prev-${
                  category.id || "section"
                } crs-carousel__arrow crs-carousel__arrow--prev`}
                aria-label="Anterior"
              >
                <IconChevronLeft size={18} />
              </button>
              <button
                className={`crs-arrow-next-${
                  category.id || "section"
                } crs-carousel__arrow crs-carousel__arrow--next`}
                aria-label="Proximo"
              >
                <IconChevronRight size={18} />
              </button>
            </>
          )}

          <Swiper {...swiperConfig}>
            {categoryVideos.map((video) => (
              <SwiperSlide key={video.id}>
                <VideoCard
                  video={video}
                  getImageSrc={getImageSrc}
                  onPlay={onVideoPlay}
                  onImageError={handleImageError}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}
    </section>
  );
};

export default React.memo(CategorySection, (prev, next) => {
  return (
    prev.category.id === next.category.id &&
    prev.category.videos?.length === next.category.videos?.length &&
    prev.category.completed_count === next.category.completed_count &&
    prev.isMobile === next.isMobile
  );
});
